/**
 * useFormAccess
 *
 * Access-control reader for a form, consumed by `FormAccessModal`.
 *
 * `isAdmin` intersects the user's own security groups with
 * `form.data.accessGroups.adminGroupIds` and never depends on the
 * `securityGroups` collection, which only feeds the assignment table.
 * `useSnapshot` tracks only the keys actually read, so while `enabled` is false
 * the `docIds`/`docs` reads are short-circuited before the dependency array can
 * evaluate them and the hook tracks nothing in that collection.
 *
 * The form read is `accessGroups` alone, off a narrow snapshot — never
 * `useFormStore()`, whose spread of `formVersion.data` re-rendered the modal on
 * every keystroke in the form. `accessGroups` genuinely feeds render (the
 * assignment table and the admin gate), so this subscription cannot be dropped
 * the way the modal's own event-time read was.
 */
import { SecurityGroup } from '#types/System.types';
import { useContext, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { UserContext } from '../providers/UserContext';
import { createCollectionStore, formStore } from '../store';

interface UseFormAccessProps {
    enabled?: boolean;
}

/**
 * Access group type indicating which level of access a group provides.
 */
export type AccessGroupType = 'readGroupIds' | 'writeGroupIds' | 'adminGroupIds';

/**
 * Represents a group with its current access level assignment.
 */
export interface GroupAccessItem {
    docId: string;
    name: string;
    accessType: AccessGroupType | null;
}

const groupsStore = createCollectionStore<SecurityGroup>('securityGroups');

/** Stable identity returned while disabled, so downstream memos never churn. */
const EMPTY_GROUPS: readonly SecurityGroup[] = Object.freeze([]);

/**
 * Checks if the user's security groups intersect with any of the provided groups.
 */
function hasGroupIntersection(securityGroups: string[], targetGroups: readonly string[] | undefined): boolean {
    if (!targetGroups || targetGroups.length === 0) return false;
    return securityGroups.some(g => targetGroups.includes(g));
}

/**
 * Hook to manage form access groups.
 *
 * Access is controlled via accessGroups on the form:
 * - readGroupIds: Groups with read access
 * - writeGroupIds: Groups with write access
 * - adminGroupIds: Groups with admin access (ACL management only, does not grant read/write)
 */
export const useFormAccess = ({ enabled = true }: UseFormAccessProps = {}) => {
    const formSnap = useSnapshot(formStore.state);
    const accessGroups = formSnap.form.data?.accessGroups;
    const { userSecurityGroups } = useContext(UserContext);

    useEffect(() => {
        if (!enabled) return;
        groupsStore.connect();
        return () => groupsStore.disconnect();
    }, [enabled]);

    const groupsSnap = useSnapshot(groupsStore.state);
    const trackedDocIds = enabled ? groupsSnap.docIds : undefined;
    const trackedDocs = enabled ? groupsSnap.docs : undefined;
    const groups = useMemo<readonly SecurityGroup[]>(() => {
        if (!trackedDocIds || !trackedDocs) return EMPTY_GROUPS;
        const result: SecurityGroup[] = [];
        for (const id of trackedDocIds) {
            const entry = trackedDocs.get(id);
            if (entry) result.push({ ...(entry.data as SecurityGroup), docId: id });
        }
        return result;
    }, [trackedDocIds, trackedDocs]);

    const isAdmin = hasGroupIntersection(userSecurityGroups, accessGroups?.adminGroupIds);

    const groupAccessList = useMemo<GroupAccessItem[]>(() => {
        const currentAccessGroups = accessGroups ?? {};

        return groups.map(group => {
            const groupId = group.docId ?? '';
            let accessType: AccessGroupType | null = null;

            if (currentAccessGroups.adminGroupIds?.includes(groupId)) {
                accessType = 'adminGroupIds';
            } else if (currentAccessGroups.writeGroupIds?.includes(groupId)) {
                accessType = 'writeGroupIds';
            } else if (currentAccessGroups.readGroupIds?.includes(groupId)) {
                accessType = 'readGroupIds';
            }

            return {
                docId: groupId,
                name: group.name || '',
                accessType,
            };
        });
    }, [groups, accessGroups]);

    return {
        groups,
        loading: groupsSnap.loading,
        groupAccessList,
        isAdmin,
    };
};
